<template>
  <v-card class="pa-4 mb-4 pyramid-card" :style="{ opacity: showLoader ? 0 : 1, transition: 'opacity 0.5s' }">
    <apexchart v-if="!showLoader && isValidPyramidData" ref="pyramidChart" type="bar" :options="pyramidChartOptions"
      :series="pyramidChartData" width="100%" :height="computedChartHeight" />

  </v-card>
</template>

<script>
import { ref, defineComponent, watch, onMounted, computed } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import moment from 'moment'

export default defineComponent({
  name: 'ReservationsPyramidChart',
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    reservations: {
      type: Array,
      required: true,
      validator: arr => Array.isArray(arr),
    },
    selectedTimeRange: {
      type: String,
      required: true,
    },
    selectedCategory: {
      type: Object,
      required: true,
      validator: obj => obj && typeof obj.name === 'string',
    },
    selectedFloor: {
      type: Object,
      required: true,
      validator: obj => obj && typeof obj.name === 'string',
    },
  },
  setup(props) {
    const showLoader = ref(true)
    const pyramidChartData = ref([])
    const pyramidChartOptions = ref({
      chart: {
        type: 'bar',
        height: 350,
        toolbar: { show: true },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '100%',
          distributed: true,
          borderRadius: 0, // Explicitly disable border radius to avoid ApexCharts error
        },
      },
      dataLabels: { enabled: true },
      xaxis: {
        categories: [],
        labels: {
          style: {
            colors: [],
            fontSize: '12px',
          },
        },
      },
      title: {
        text: 'Reservations Distribution',
        align: 'center',
      },
      colors: [],
    })
    const isValidPyramidData = computed(() => {
      return (
        Array.isArray(pyramidChartData.value) &&
        pyramidChartData.value.length > 0 &&
        Array.isArray(pyramidChartData.value[0].data) &&
        pyramidChartData.value[0].data.length > 0
      )
    })
    const makeColor = (colorNum, colors) => {
      try {
        if (colors < 1) colors = 1 // Avoid divide by zero
        return `hsl(${(colorNum * (360 / colors)) % 360}, 100%, 50%)`
      } catch {
        return '#000000' // Fallback color
      }
    }

    const filterReservations = () => {
      try {
        if (!Array.isArray(props.reservations)) return []

        let startDate, endDate
        switch (props.selectedTimeRange) {
          case 'today':
            startDate = moment().startOf('day')
            endDate = moment().endOf('day')
            break
          case 'yesterday':
            startDate = moment().subtract(1, 'day').startOf('day')
            endDate = moment().subtract(1, 'day').endOf('day')
            break
          case 'tomorrow':
            startDate = moment().add(1, 'day').startOf('day')
            endDate = moment().add(1, 'day').endOf('day')
            break
          case 'thisWeek':
            startDate = moment().startOf('week')
            endDate = moment().endOf('week')
            break
          case 'lastWeek':
            startDate = moment().subtract(1, 'week').startOf('week')
            endDate = moment().subtract(1, 'week').endOf('week')
            break
          case 'currentMonth':
            startDate = moment().startOf('month')
            endDate = moment().endOf('month')
            break
          case 'lastMonth':
            startDate = moment().subtract(1, 'month').startOf('month')
            endDate = moment().subtract(1, 'month').endOf('month')
            break
          case 'nextMonth':
            startDate = moment().add(1, 'month').startOf('month')
            endDate = moment().add(1, 'month').endOf('month')
            break
          default:
            return []
        }

        let filteredReservations = props.reservations.filter(reservation => {
          if (!reservation?.startTime) return false
          const reservationDate = moment(reservation.startTime)
          return reservationDate.isValid() && reservationDate.isBetween(startDate, endDate, null, '[]')
        })

        if (props.selectedCategory?.name && props.selectedCategory.name !== 'All Categories') {
          filteredReservations = filteredReservations.filter(reservation =>
            reservation?.poiCategory === props.selectedCategory.name
          )
        }

        if (props.selectedFloor?.name && props.selectedFloor.name !== 'All Floors') {
          filteredReservations = filteredReservations.filter(reservation =>
            reservation?.floorName === props.selectedFloor.name
          )
        }

        return filteredReservations.map(reservation => ({
          ...reservation,
          poiCategory: reservation?.poiCategory || 'Unknown',
          poiName: reservation?.poiName || 'Unknown',
          floorName: reservation?.floorName || 'Unknown',
          startTime: reservation?.startTime || moment().toISOString(),
        }))
      } catch {
        return []
      }
    }

    const processPyramidData = () => {
      try {
        const filteredReservations = filterReservations()
        if (!filteredReservations.length) {
          pyramidChartData.value = []
          pyramidChartOptions.value.xaxis.categories = []
          pyramidChartOptions.value.colors = []
          return
        }

        const dataByGroup = {}
        if (props.selectedCategory?.name === 'All Categories') {
          filteredReservations.forEach(reservation => {
            if (!reservation?.poiCategory) return
            const category = reservation.poiCategory
            dataByGroup[category] = (dataByGroup[category] || 0) + 1
          })
        } else {
          filteredReservations.forEach(reservation => {
            if (!reservation?.poiName) return
            const poi = reservation.poiName
            dataByGroup[poi] = (dataByGroup[poi] || 0) + 1
          })
        }

        const sortedData = Object.entries(dataByGroup)
          .filter(([_, count]) => typeof count === 'number' && !isNaN(count) && count >= 0)
          .sort((a, b) => b[1] - a[1])
          .map(([group, count]) => ({ group, count }))

        pyramidChartData.value = [
          {
            name: 'Reservations',
            data: sortedData.map(item => item.count).filter(val => typeof val === 'number' && !isNaN(val)),
          },
        ]

        const colorsCount = sortedData.length
        pyramidChartOptions.value.xaxis.categories = sortedData.map(item => item.group)
        pyramidChartOptions.value.colors = sortedData.map((_, index) => makeColor(index, colorsCount))
        pyramidChartOptions.value.xaxis.labels.style.colors = pyramidChartOptions.value.colors
      } catch {
        pyramidChartData.value = []
        pyramidChartOptions.value.xaxis.categories = []
        pyramidChartOptions.value.colors = []
      }
    }

    const computedChartHeight = computed(() => {
      try {
        const categoriesCount = pyramidChartOptions.value.xaxis.categories.length
        if (categoriesCount > 16) return 'auto'
        if (categoriesCount > 8) return 700
        return 350
      } catch {
        return 350 // Fallback height
      }
    })

    watch([() => props.selectedTimeRange, () => props.selectedCategory, () => props.selectedFloor], () => {
      try {
        showLoader.value = true
        setTimeout(() => {
          try {
            processPyramidData()
            showLoader.value = false
          } catch {
            pyramidChartData.value = []
            showLoader.value = false
          }
        }, 500)
      } catch {
        showLoader.value = false
      }
    }, { deep: true })

    onMounted(() => {
      try {
        processPyramidData()
        showLoader.value = false
      } catch {
        pyramidChartData.value = []
        showLoader.value = false
      }
    })

    return {
      showLoader,
      pyramidChartOptions,
      pyramidChartData,
      computedChartHeight,
      isValidPyramidData,
    }
  },
})
</script>

<style scoped>
.pyramid-card {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}
</style>